/* OccupancyIntelligenceTab — Stays Ketti (10 rooms).
 * Shows: today occupancy/ADR/RevPAR/TRevPAR, 7-day trend, live room board,
 * 30-day source mix (commission-adjusted), 7-day forward forecast. */
import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { BedDouble, AlertCircle } from 'lucide-react';

const INR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const STATUS_COLOR = {
  vacant_clean: { bg: '#ECFDF5', border: '#10B981', label: 'Vacant Clean' },
  vacant_dirty: { bg: '#FEF3C7', border: '#F59E0B', label: 'Vacant Dirty' },
  occupied:     { bg: '#EFF6FF', border: '#3B82F6', label: 'Occupied' },
  maintenance:  { bg: '#FEE2E2', border: '#DC2626', label: 'Maintenance' },
  out_of_order: { bg: '#F3F4F6', border: '#9CA3AF', label: 'Out of Order' },
};

const SOURCE_LABEL = {
  direct: 'Direct', booking_com: 'Booking.com', makemytrip: 'MakeMyTrip',
  airbnb: 'Airbnb', goibibo: 'Goibibo', google_hotels: 'Google Hotels',
  agoda: 'Agoda', walk_in: 'Walk-in', phone: 'Phone', corporate: 'Corporate',
  repeat: 'Repeat', referral: 'Referral',
};

export default function OccupancyIntelligenceTab() {
  const [summary, setSummary] = useState(null);
  const [rooms, setRooms] = useState(null);
  const [sourceMix, setSourceMix] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, r, m, f] = await Promise.all([
          api.get('/outlet-intel/stays/occupancy/summary'),
          api.get('/outlet-intel/stays/occupancy/room-status'),
          api.get('/outlet-intel/stays/occupancy/source-mix?days=30'),
          api.get('/outlet-intel/stays/occupancy/forecast-7d'),
        ]);
        if (cancelled) return;
        setSummary(s.data); setRooms(r.data); setSourceMix(m.data); setForecast(f.data);
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="text-sm" style={{ color: '#6B7280' }}>Loading occupancy intelligence…</div>;
  if (!summary) return <div className="text-sm" style={{ color: '#DC2626' }}>Failed to load.</div>;

  const t = summary.today || {};

  return (
    <div className="space-y-4" data-testid="occupancy-intel">
      {/* Today KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Occupancy" value={`${t.occupancy_pct || 0}%`}
             sub={`${t.rooms_sold || 0}/${t.rooms_available || 10} rooms`}
             highlight testid="kpi-occ" />
        <Kpi label="ADR" value={INR(t.adr)} sub="Avg Daily Rate" testid="kpi-adr" />
        <Kpi label="RevPAR" value={INR(t.revpar)} sub="Revenue per avail. room" testid="kpi-revpar" />
        <Kpi label="TRevPAR" value={INR(t.trevpar)} sub="Total incl. F&B/add-ons" testid="kpi-trevpar" />
        <Kpi label="Room Revenue Today" value={INR(t.room_revenue)} sub="Rack-rate sum" testid="kpi-room-rev" />
      </div>

      {/* 7-day trend */}
      <Card title="Last 7 Nights — Occupancy, ADR, RevPAR" testid="card-7d-trend">
        <TrendTable rows={summary.seven_day_trend || []} />
      </Card>

      {/* Live room status grid */}
      <Card title={`Live Room Status (${rooms?.total || 10} rooms)`} testid="card-room-grid">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {(rooms?.rooms || []).map(r => (
            <RoomTile key={r.room_number} room={r} />
          ))}
        </div>
      </Card>

      {/* Source mix */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Booking Source Mix (30d)" testid="card-source">
          <div className="mb-2 flex justify-between text-[11px]">
            <span style={{ color: '#6B7280' }}>Gross revenue</span>
            <span className="font-semibold">{INR(sourceMix?.total_gross)}</span>
          </div>
          <div className="mb-3 flex justify-between text-[11px]">
            <span style={{ color: '#6B7280' }}>Net after OTA commission</span>
            <span className="font-semibold" style={{ color: '#10B981' }}>{INR(sourceMix?.total_net)}</span>
          </div>
          <div className="mb-3 text-[11px] px-2 py-1.5 rounded" style={{
            backgroundColor: '#FEF3C7', color: '#92400E',
          }}>
            <AlertCircle size={11} className="inline mr-1" />
            OTA commission drag: {INR((sourceMix?.total_gross || 0) - (sourceMix?.total_net || 0))}
          </div>
          {(sourceMix?.rows || []).map(r => (
            <SourceRow key={r.source} row={r} />
          ))}
        </Card>

        <Card title="7-Day Forward Forecast" testid="card-forecast">
          {(forecast?.rows || []).map(r => (
            <ForecastRow key={r.date} row={r} />
          ))}
        </Card>
      </div>

      <Card title="Occupancy Signals" testid="card-signals">
        <Signals summary={summary} sourceMix={sourceMix} forecast={forecast} />
      </Card>
    </div>
  );
}

function Kpi({ label, value, sub, highlight, testid }) {
  return (
    <div className="rounded-xl p-4" style={{
      backgroundColor: highlight ? '#1B2A4A' : 'white',
      color: highlight ? 'white' : '#1B2A4A',
      border: highlight ? 'none' : '1px solid #E5E1DC',
    }} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider mb-1"
        style={{ color: highlight ? '#9DB5CC' : '#6B7280' }}>{label}</p>
      <p className="text-2xl font-semibold">{value}</p>
      <p className="text-[11px] mt-1" style={{ color: highlight ? '#9DB5CC' : '#6B7280' }}>{sub}</p>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl bg-white p-5" style={{ border: '1px solid #E5E1DC' }} data-testid={testid}>
      <h3 className="text-sm font-semibold mb-3" style={{ color: '#1B2A4A' }}>{title}</h3>
      {children}
    </div>
  );
}

function TrendTable({ rows }) {
  return (
    <div className="space-y-1">
      <div className="grid grid-cols-6 gap-2 text-[10px] uppercase pb-1 mb-1" style={{ color: '#6B7280', borderBottom: '1px solid #E5E1DC' }}>
        <span>Night</span><span className="text-right">Sold</span>
        <span className="text-right">Occ %</span><span className="text-right">ADR</span>
        <span className="text-right">RevPAR</span><span className="text-right">TRevPAR</span>
      </div>
      {rows.map(r => (
        <div key={r.date} className="grid grid-cols-6 gap-2 text-[12px] py-1">
          <span style={{ color: '#1B2A4A' }}>{r.day_label}</span>
          <span className="text-right">{r.rooms_sold}/{r.rooms_available}</span>
          <span className="text-right font-semibold">{r.occupancy_pct}%</span>
          <span className="text-right">{INR(r.adr)}</span>
          <span className="text-right">{INR(r.revpar)}</span>
          <span className="text-right">{INR(r.trevpar)}</span>
        </div>
      ))}
    </div>
  );
}

function RoomTile({ room }) {
  const s = STATUS_COLOR[room.status] || STATUS_COLOR.out_of_order;
  return (
    <div className="rounded-lg p-2.5" style={{
      backgroundColor: s.bg, borderLeft: `3px solid ${s.border}`,
    }} data-testid={`room-${room.room_number}`}>
      <div className="flex items-baseline justify-between">
        <span className="text-[14px] font-semibold">{room.room_number}</span>
        <span className="text-[9px] uppercase" style={{ color: s.border }}>{room.room_type}</span>
      </div>
      <p className="text-[10px] mt-1" style={{ color: '#6B7280' }}>{s.label}</p>
      {room.current_occupant && (
        <p className="text-[10px] mt-1" style={{ color: '#1B2A4A' }}>
          <BedDouble size={10} className="inline mr-0.5" />
          {room.current_occupant.guest_name?.split(' ')[0] || 'Guest'}
          <span className="opacity-60"> · {SOURCE_LABEL[room.current_occupant.source] || room.current_occupant.source}</span>
        </p>
      )}
    </div>
  );
}

function SourceRow({ row }) {
  return (
    <div className="py-2 flex items-center gap-3 text-[12px]">
      <div className="flex-1 min-w-0">
        <div className="flex justify-between mb-0.5">
          <span className="font-medium">{SOURCE_LABEL[row.source] || row.source}</span>
          <span>{INR(row.gross_revenue)} <span className="opacity-60">({row.share_pct}%)</span></span>
        </div>
        <div className="flex justify-between text-[10px]" style={{ color: '#6B7280' }}>
          <span>{row.bookings} bookings · {row.commission_pct}% comm</span>
          <span>net {INR(row.net_revenue)}</span>
        </div>
      </div>
    </div>
  );
}

function ForecastRow({ row }) {
  const pct = row.firm_occupancy_pct;
  const color = pct >= 70 ? '#10B981' : pct >= 40 ? '#F59E0B' : '#6B7280';
  return (
    <div className="py-2 flex items-center gap-3 text-[12px]" data-testid={`forecast-${row.date}`}>
      <span className="w-20 font-medium">{row.day_label}</span>
      <div className="flex-1 h-5 rounded overflow-hidden relative" style={{ backgroundColor: '#F3F4F6' }}>
        <div className="h-full flex items-center justify-end pr-1.5 text-[10px] text-white font-semibold"
          style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }}>
          {pct > 15 && `${pct}%`}
        </div>
      </div>
      <span className="text-[11px] w-24 text-right" style={{ color: '#6B7280' }}>
        {row.firm_bookings} firm{row.tentative_bookings > 0 ? ` +${row.tentative_bookings}` : ''}
      </span>
    </div>
  );
}

function Signals({ summary, sourceMix, forecast }) {
  const signals = [];
  const t = summary?.today || {};
  if (t.occupancy_pct < 50) signals.push({ tone: 'warn', text: `Only ${t.occupancy_pct}% occupied today — push a same-night flash rate.` });
  if (t.occupancy_pct > 90) signals.push({ tone: 'good', text: `${t.occupancy_pct}% full — consider rate hold or up-sell.` });
  const directRow = sourceMix?.rows?.find(r => r.source === 'direct');
  const directShare = directRow?.share_pct || 0;
  if (directShare < 30) signals.push({ tone: 'info', text: `Direct share ${directShare}% — OTA-dependent. Every 5% shift to direct saves ~${INR((sourceMix?.total_gross || 0) * 0.05 * 0.15)}/mo.` });
  const dryDays = (forecast?.rows || []).filter(r => r.firm_occupancy_pct < 40);
  if (dryDays.length >= 2) signals.push({ tone: 'warn', text: `${dryDays.length} days in next 7 below 40% firm — trigger Direct campaign for ${dryDays.map(d => d.day_label).join(', ')}.` });
  if (!signals.length) return <p className="text-[12px]" style={{ color: '#6B7280' }}>Rooms running steady. No flags.</p>;
  return (
    <div className="space-y-2">
      {signals.map((s, i) => (
        <div key={i} className="flex items-start gap-2 text-[12px] p-2.5 rounded-lg" style={{
          backgroundColor: s.tone === 'warn' ? '#FEF3C7' : s.tone === 'good' ? '#ECFDF5' : '#EFF6FF',
          color: s.tone === 'warn' ? '#92400E' : s.tone === 'good' ? '#065F46' : '#1E40AF',
        }}>
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <span>{s.text}</span>
        </div>
      ))}
    </div>
  );
}
